import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { CustomTable } from "../../CommonComponent/Table";
import Loader from "../../../Loader";
import { PREVIEW_URL, VIPER_CONST } from "../../../commonConstant";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  publish,
  rejectButton,
  publishButton,
} from "../../../slices/publisher/publisherSlice";
import { showConfirm, showError, showReject, showSuccess } from "../../Swal";
import { getRedirectUrl, HandleAPIError } from "../../../commonFunction";
import { authSessionid, authUsername } from "../../../slices/auth-slice";
import { IThemeUpdate } from "../../../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faEye,
  faPaperPlane,
  faTrash,
  faUpload,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

function PublisherListing(): JSX.Element {
  const dispatch = useAppDispatch();
  const getPublisher = useAppSelector((state) => state.publish.publisher);
  const [loading, setLoading] = useState(true);
  const useDataRej = useAppSelector((state) => state.publish);
  const sessionId = useAppSelector(authSessionid);
  const userName = useAppSelector(authUsername);
  const redirectDomain = getRedirectUrl()
  console.log("data", redirectDomain);

  const columnHelper = createColumnHelper();
  const COLUMNS = [
    columnHelper.accessor("distributorname", {
      cell: (info) => (
        <div style={{ textAlign: "left" }}>{info.getValue()}</div>
      ),
      header: "Distributor Name",
    }),
    columnHelper.accessor("name", {
      cell: (info) => (
        <div style={{ textAlign: "left" }}>{info.getValue()}</div>
      ),
      header: "Name",
    }),
    columnHelper.accessor("entryby", {
      cell: (info) => (
        <div style={{ textAlign: "left" }}>{info.getValue()}</div>
      ),
      header: "Entry by",
    }),
    columnHelper.accessor("entrydate", {
      cell: (info) => (
        <div style={{ textAlign: "left" }}>{info.getValue()}</div>
      ),
      header: "Entry Date",
    }),
    columnHelper.accessor("view", {
      cell: (info) => (
        <div style={{ textAlign: "center" }} >
          <OverlayTrigger
            placement="bottom"
            overlay={
                <Tooltip
                id="tooltip-cart"
                className="tooltip fixed-tooltip"
                style={{
                  fontSize: "15px",
                }}
                >
                Preview
                </Tooltip>
            }
          >
            <FontAwesomeIcon
              className="ms/1 cursor-pointer icon-color icons-color mx-2"
              icon={faEye}
              size="lg"
              onClick={() => handlePreviewButtonClick(info.row.original)}
            />
          </OverlayTrigger>

          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip
                id="tooltip-cart"
                className="tooltip fixed-tooltip"
                style={{
                  fontSize: "15px",
                }}
              >
                Publish
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              className="ms/1 cursor-pointer icon-color icons-color mx-2"
              icon={faPaperPlane}
              size="lg"
              onClick={() => handlePublishButtonClick(info.row.original)}
            />
          </OverlayTrigger>

          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip
                id="tooltip-cart"
                className="tooltip fixed-tooltip"
                style={{
                  fontSize: "15px",
                }}
              >
                Reject
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              className="ms/1 cursor-pointer icon-color icons-color mx-2"
              size="lg"
              icon={faBan}
              onClick={() => handleRejectButtonClick(info.row.original)}
            />
          </OverlayTrigger>
        </div>
      ),
      header: "",
      enableSorting: false,
    }),
  ];

  const handlePreviewButtonClick = (row: any) => {
    window.open(
      `${redirectDomain}/affiliate/${row?.affiliateid}?preview=true`,
      "_blank"
    );
  };

  const handlePublishButtonClick = async (row: any) => {
    let confirmed = await showConfirm(
      "Confirm",
      "Are you sure you want to publish the theme?"
    );
    if (confirmed.isConfirmed) {
      try {
        const request: IThemeUpdate = {
          sessionid: sessionId,
          username: userName,
          request: {
            affiliateid: row?.affiliateid,
            action: "P",
          },
        };
        setLoading(true);
        const response = await dispatch(publishButton(request)).unwrap();

        const data: IThemeUpdate = {
          sessionid: sessionId,
          username: userName,
          request: {},
        };

        if (response?.status === 0) {
          showSuccess("Success", "Theme published successfully");
          setLoading(false);
          await dispatch(publish(data));
        } else {
          showError("Error", useDataRej?.statusMessage);
        }
      } catch (error) {
        HandleAPIError(error);
      }
    }
  };

  const handleRejectButtonClick = async (row: any) => {
    let confirmed = await showReject(
      "Remarks",
      row?.entryby,
      row.distributorname
    );

    if (confirmed.isConfirmed) {
      try {
        const request: IThemeUpdate = {
          sessionid: sessionId,
          username: userName,
          request: {
            affiliateid: row?.affiliateid,
            action: "R",
            message: confirmed?.value,
          },
        };
        setLoading(true);
        const response = await dispatch(rejectButton(request)).unwrap();

        const data: IThemeUpdate = {
          sessionid: sessionId,
          username: userName,
          request: {},
        };

        if (response?.status === 0) {
          showSuccess(
            "Success",
            response?.statusMessage || "Theme rejected successfully!"
          );
          setLoading(false);
          await dispatch(publish(data));
        } else {
          showError("Error", response?.statusMessage || "Error");
        }
      } catch (error) {
        HandleAPIError(error);
      }
    }
  };

  useEffect(() => {
    const data: IThemeUpdate = {
      sessionid: sessionId,
      username: userName,
      request: {},
    };

    const fetchData = async () => {
      setLoading(true);
      await dispatch(publish(data));
      setLoading(false);
    };
    fetchData();
  }, [dispatch]);

  return (
    <>
      <Loader loading={loading} />
      <Container className="py-3 h-100">
        <h3 className="text-center py-2 text-black mb-5 underline fs-3 ">
          <span className="border-bottom-line text-uppercase">
            Publish Request
          </span>
        </h3>
        <Card className="shadow mt-4">
          <CardBody className="p-4 overflow-hidden ">
            <CustomTable
              columns={COLUMNS}
              data={getPublisher.data || []}
              noDataMessage={"No Data Available"}
            />
          </CardBody>
        </Card>
      </Container>
    </>
  );
}

export default PublisherListing;
